import { useEffect, useState } from 'react';
import { Button, Input, PasswordInput, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal } from '../../../shared/components';
import { PlusIcon, TrashIcon, DashboardIcon, EditIcon, ResetIcon } from '../../../shared/icons';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { ApplicationMember, PersonLookup } from '../../../shared/services/settings/types';
import { LccRole } from '../../../shared/auth/types';
import { ApiError } from '../../../shared/services/http';
import { AssignModulesModal } from './AssignModulesModal';
import { ASSIGNABLE_MODULES, ROLE_DEFAULT_MODULES } from '../../../shared/config/modules';
import { HR_ASSIGNABLE_MODULES, HR_ROLE_DEFAULT_MODULES } from '../../../shared/config/hrModules';
import { useApplicationContext } from '../../../shared/auth/ApplicationContext';

const repo = getSettingsRepository();

const ROLE_OPTIONS = [
  { value: 'operator', label: 'Operador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'admin', label: 'Administrador' },
];

const emptyForm = { fullName: '', email: '', password: '', role: 'operator' as LccRole };

/**
 * Tabla de parametrizacion: Usuarios de LA APLICACION ACTIVA (Control
 * Operativo o Gestion Humana - ver useApplicationContext). "Agregar" crea la
 * cuenta de acceso REAL (correo + clave, via Clerk) Y otorga de una vez el
 * acceso a esta aplicacion puntual; si la persona ya existe en la plataforma
 * (por tener acceso a OTRA aplicacion), usa el mismo correo para agregarle
 * acceso a esta sin tocar el que ya tenia.
 *
 * "Quitar" NUNCA borra a la persona de la plataforma: revoca su acceso a
 * ESTA aplicacion (revokeApplicationAccess) - su acceso a otras aplicaciones
 * de la Suite no se ve afectado. Borrar a alguien de la plataforma por
 * completo es una accion mas seria, fuera del alcance de esta pantalla.
 */
export function UsersTab() {
  const toast = useToast();
  const { applicationCode } = useApplicationContext();
  const isHr = applicationCode === 'gestion-humana';
  const assignableModules = isHr ? HR_ASSIGNABLE_MODULES : ASSIGNABLE_MODULES;
  const roleDefaultModules = isHr ? HR_ROLE_DEFAULT_MODULES : ROLE_DEFAULT_MODULES;

  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [revoking, setRevoking] = useState<ApplicationMember | null>(null);
  const [assigning, setAssigning] = useState<ApplicationMember | null>(null);
  const [editing, setEditing] = useState<ApplicationMember | null>(null);
  const [editForm, setEditForm] = useState({ fullName: '', email: '' });
  const [resetting, setResetting] = useState<ApplicationMember | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [existing, setExisting] = useState<NonNullable<PersonLookup['person']> | null>(null);

  const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

  const openEdit = (u: ApplicationMember) => {
    setEditForm({ fullName: u.fullName, email: u.email ?? '' });
    setEditing(u);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const patch: { fullName?: string; email?: string } = {};
    if (editForm.fullName.trim() && editForm.fullName.trim().toUpperCase() !== editing.fullName.toUpperCase()) patch.fullName = editForm.fullName.trim();
    if (editForm.email.trim().toLowerCase() !== (editing.email ?? '').toLowerCase()) patch.email = editForm.email.trim();
    if (!patch.fullName && !patch.email) { setEditing(null); return; }
    setIsBusy(true);
    try {
      await repo.updateUserProfile(editing.id, patch);
      toast.show('Datos actualizados.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudieron actualizar los datos.'), 'error');
    } finally {
      setIsBusy(false);
    }
  };

  const savePassword = async () => {
    if (!resetting) return;
    if (newPassword.length < 8) { toast.show('La clave debe tener mínimo 8 caracteres.', 'error'); return; }
    setIsBusy(true);
    try {
      await repo.resetUserPassword(resetting.id, newPassword);
      toast.show(`Clave de ${resetting.fullName} actualizada. Debe usar la nueva en su próximo ingreso.`);
      setResetting(null);
      setNewPassword('');
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudo restablecer la clave.'), 'error');
    } finally {
      setIsBusy(false);
    }
  };

  const load = () => {
    if (!applicationCode) return;
    repo.listApplicationMembers(applicationCode, true).then((u) => { setMembers(u); setIsLoading(false); });
  };
  useEffect(() => { load(); }, [applicationCode]);

  const addUser = async () => {
    if (!form.fullName.trim() || !form.email.trim() || !form.password) {
      toast.show('Nombre, correo y clave son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      // Si el correo ya existe en la plataforma (por otra aplicacion) NO se crea otra cuenta: se ofrece darle acceso aqui.
      const lookup = applicationCode ? await repo.lookupPersonByEmail(applicationCode, form.email.trim()) : { found: false };
      if (lookup.found && lookup.person) {
        if (lookup.person.hasAccessHere) toast.show(`${lookup.person.fullName} ya tiene acceso a esta aplicación.`, 'error');
        else setExisting(lookup.person);
        return;
      }
      await repo.createUser({ fullName: form.fullName.trim(), email: form.email.trim(), password: form.password, role: form.role });
      setForm(emptyForm);
      toast.show('Usuario creado. Ya puede iniciar sesión con ese correo y clave.');
      load();
    } catch (err) {
      // Ej: "Ya existe un usuario con ese correo" o el motivo que devuelva Clerk
      // (clave muy corta/comun, correo invalido, etc).
      const message = err instanceof ApiError ? err.message : 'No se pudo crear el usuario.';
      toast.show(message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const grantExisting = async () => {
    if (!existing || !applicationCode) return;
    setIsSaving(true);
    try {
      await repo.grantApplicationAccess(applicationCode, existing.id, form.role, null);
      toast.show(`${existing.fullName} ahora tiene acceso con su clave actual.`);
      setExisting(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.show(errorMessage(err, 'No se pudo dar el acceso.'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const grantAccess = async (u: ApplicationMember, role: LccRole = 'operator') => {
    if (!applicationCode) return;
    await repo.grantApplicationAccess(applicationCode, u.id, role, null);
    toast.show(`${u.fullName} ahora tiene acceso.`);
    load();
  };

  const changeRole = async (u: ApplicationMember, role: LccRole) => {
    if (!applicationCode) return;
    await repo.grantApplicationAccess(applicationCode, u.id, role, u.allowedModules);
    toast.show(`Rol de ${u.fullName} actualizado.`);
    load();
  };

  const saveModules = async (id: string, allowedModules: string[] | null) => {
    if (!applicationCode) return;
    const member = members.find((m) => m.id === id);
    await repo.grantApplicationAccess(applicationCode, id, member?.role ?? 'operator', allowedModules);
    toast.show('Pantallas actualizadas.');
    load();
  };

  const columns: Column<ApplicationMember>[] = [
    { key: 'name', header: 'Nombre', render: (u) => u.fullName, sortable: true, sortValue: (u) => u.fullName },
    { key: 'email', header: 'Correo', render: (u) => <span className="text-ink-500">{u.email ?? '—'}</span> },
    {
      key: 'role',
      header: 'Rol',
      render: (u) =>
        u.hasAccess ? (
          <Select options={ROLE_OPTIONS} value={u.role} onChange={(e) => changeRole(u, e.target.value as LccRole)} className="h-9 min-w-[150px] text-xs" />
        ) : (
          <Button size="md" variant="outline" onClick={() => grantAccess(u)}>
            Dar acceso
          </Button>
        ),
    },
    {
      key: 'modules',
      header: 'Pantallas',
      render: (u) =>
        u.hasAccess ? (
          <button onClick={() => setAssigning(u)} className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline">
            <DashboardIcon width={14} height={14} />
            {u.allowedModules && u.allowedModules.length > 0 ? u.allowedModules.map((id) => assignableModules.find((m) => m.id === id)?.label ?? id).join(', ') : 'Por defecto del rol'}
          </button>
        ) : (
          <span className="text-xs text-ink-300">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Acceso',
      render: (u) => <Badge tone={u.hasAccess ? 'success' : 'neutral'}>{u.hasAccess ? 'Con acceso' : 'Sin acceso'}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <div className="flex items-center gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar nombre y correo" onClick={() => openEdit(u)} className="h-9 w-9" />
          <IconButton icon={<ResetIcon width={16} height={16} />} label="Restablecer clave" onClick={() => { setNewPassword(''); setResetting(u); }} className="h-9 w-9" />
          {u.hasAccess && (
            <IconButton icon={<TrashIcon width={16} height={16} />} label="Quitar acceso" onClick={() => setRevoking(u)} className="h-9 w-9 text-critical hover:bg-red-50" />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input placeholder="Nombre del colaborador" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} />
        <Input placeholder="Correo (para iniciar sesión)" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <PasswordInput placeholder="Clave (mín. 8 caracteres)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
        <Select options={ROLE_OPTIONS} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as LccRole }))} />
      </div>
      <Button size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={addUser} isLoading={isSaving}>
        Agregar
      </Button>
      {existing && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-ink-700">
          <p>
            Ese correo ya existe en la plataforma: <strong>{existing.fullName}</strong>
            {existing.applications.length > 0 ? ` (tiene acceso a ${existing.applications.join(', ')})` : ''}. No se crea otra cuenta: puedes darle acceso a esta aplicación con el rol elegido y entrará con la clave que ya tiene.
          </p>
          <div className="mt-2 flex gap-2">
            <Button size="md" onClick={grantExisting} isLoading={isSaving}>Dar acceso con su clave actual</Button>
            <Button size="md" variant="outline" onClick={() => setExisting(null)}>Cancelar</Button>
          </div>
        </div>
      )}
      <p className="text-xs text-ink-400">
        Esta lista muestra <strong>solo a las personas de esta aplicación</strong>. Al agregar, se crea la cuenta de acceso y su acceso a esta aplicación (ya puede iniciar sesión con ese correo y clave).
        Si el correo ya existe en la plataforma, se le da acceso aquí sin cambiarle la clave ni el acceso que ya tenía. Usa "Pantallas" en cada fila para asignar un set distinto a un usuario puntual.
      </p>
      <DataTable columns={columns} rows={members} rowKey={(u) => u.id} isLoading={isLoading} emptyTitle="Sin usuarios" />
      <ConfirmDialog
        open={!!revoking}
        title="Quitar acceso"
        description={`¿Quitar el acceso de ${revoking?.fullName} a esta aplicación? Su acceso a otras aplicaciones de la Suite no se ve afectado.`}
        tone="danger"
        confirmLabel="Quitar acceso"
        onCancel={() => setRevoking(null)}
        onConfirm={async () => {
          if (revoking && applicationCode) await repo.revokeApplicationAccess(applicationCode, revoking.id);
          setRevoking(null);
          load();
        }}
      />
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Editar usuario">
        <div className="space-y-4">
          <Input placeholder="Nombre del colaborador" value={editForm.fullName} onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))} />
          <Input placeholder="Correo (para iniciar sesión)" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
          <p className="text-xs text-ink-400">El cambio aplica a la persona en toda la Suite (Control Operativo, Gestión Humana y TurnoGeo). Si cambias el correo, deberá iniciar sesión con el nuevo.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
            <Button fullWidth onClick={saveEdit} isLoading={isBusy}>Guardar</Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!resetting} onClose={() => setResetting(null)} title="Restablecer clave">
        <div className="space-y-4">
          <p className="text-sm text-ink-600">Usuario: <span className="font-semibold">{resetting?.fullName}</span></p>
          <PasswordInput placeholder="Nueva clave (mín. 8 caracteres)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <p className="text-xs text-ink-400">Reemplaza su clave de inmediato y cierra sus otras sesiones abiertas. Entrégasela por un medio seguro. Aplica a toda la Suite.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setResetting(null)}>Cancelar</Button>
            <Button fullWidth onClick={savePassword} isLoading={isBusy}>Restablecer</Button>
          </div>
        </div>
      </Modal>
      <AssignModulesModal user={assigning} onClose={() => setAssigning(null)} onSave={saveModules} assignableModules={assignableModules} roleDefaultModules={roleDefaultModules} />
    </div>
  );
}
